import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, CircleCheck, Scale } from "lucide-react";
import { Section } from "./ui/section";
import { Skeleton } from "./ui/skeleton";
import { Avatar } from "./ui/avatar";
import { MoneyAmount } from "./MoneyAmount";
import { cn } from "../lib/utils";
import { useGroupBalances } from "../api/hooks";
import type { Group } from "../api/types";

/**
 * "Who owes who, simplified" — the greedy transfer plan from the balances
 * endpoint, plus an expandable per-participant net breakdown. Each suggested
 * transfer links to the record-settlement form pre-filled with payer, payee,
 * and amount, so settling a specific debt is one tap.
 */
export function BalancesBreakdown({
  group,
  canSettle,
}: {
  group: Group;
  canSettle: boolean;
}): ReactNode {
  const balancesQ = useGroupBalances(group.id);
  const [showDetail, setShowDetail] = useState(false);

  const nameOf = (id: string): string =>
    group.participants.find((p) => p.id === id)?.displayName ?? "Someone";

  if (balancesQ.isLoading) {
    return (
      <Section title="Balances">
        <Skeleton className="h-24 rounded-md" />
      </Section>
    );
  }

  const data = balancesQ.data;
  if (!data) {
    return (
      <Section title="Balances">
        <p className="text-body-md text-on-surface-variant">
          Couldn't load balances. Please try again.
        </p>
      </Section>
    );
  }

  const suggestions = data.suggestions;
  const nonZero = data.balances.filter((b) => b.net !== 0);

  return (
    <Section
      title="Who owes who"
      description="The simplest set of payments to settle everyone up."
    >
      {suggestions.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <span className="grid size-14 place-items-center rounded-full bg-tertiary-container text-on-tertiary-container">
            <CircleCheck className="size-6" />
          </span>
          <p className="text-body-md text-on-surface-variant">
            Everyone is settled up. Nothing to pay.
          </p>
        </div>
      ) : (
        <ul className="space-y-stack-sm">
          {suggestions.map((s, i) => {
            const row = (
              <div className="flex items-center justify-between gap-4 rounded-md border border-outline-variant/60 p-4 transition-colors hover:border-primary/50 hover:bg-surface-container/40">
                <div className="flex min-w-0 items-center gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <Avatar name={nameOf(s.from)} size="sm" />
                    <span className="truncate text-body-md">
                      {nameOf(s.from)}
                    </span>
                  </div>
                  <ArrowRight
                    className="size-4 shrink-0 text-on-surface-variant"
                    aria-label="pays"
                  />
                  <div className="flex min-w-0 items-center gap-2">
                    <Avatar name={nameOf(s.to)} size="sm" />
                    <span className="truncate text-body-md">
                      {nameOf(s.to)}
                    </span>
                  </div>
                </div>
                <MoneyAmount
                  minor={s.amount}
                  currency={data.currency}
                  className="shrink-0 text-headline-md"
                />
              </div>
            );
            return (
              <li key={`${s.from}-${s.to}-${i}`}>
                {canSettle ? (
                  <Link
                    to={`/groups/${group.id}/settlements/new?from=${s.from}&to=${s.to}&amount=${s.amount}`}
                    aria-label={`Record ${nameOf(s.from)} paying ${nameOf(
                      s.to,
                    )}`}
                  >
                    {row}
                  </Link>
                ) : (
                  row
                )}
              </li>
            );
          })}
        </ul>
      )}

      {nonZero.length > 0 ? (
        <div className="border-t border-outline-variant/40 pt-4">
          <button
            type="button"
            onClick={() => setShowDetail((v) => !v)}
            aria-expanded={showDetail}
            className="flex w-full items-center justify-between gap-2 text-label-md text-on-surface-variant transition-colors hover:text-on-surface"
          >
            <span className="flex items-center gap-2">
              <Scale className="size-4" aria-hidden />
              {showDetail ? "Hide" : "Show"} each person's balance
            </span>
            <ChevronDown
              className={cn(
                "size-4 transition-transform",
                showDetail && "rotate-180",
              )}
              aria-hidden
            />
          </button>
          {showDetail ? (
            <ul className="mt-4 space-y-2">
              {nonZero
                .slice()
                .sort((a, b) => b.net - a.net)
                .map((b) => {
                  const owed = b.net > 0;
                  return (
                    <li
                      key={b.participantId}
                      className="flex items-center justify-between gap-3 py-1"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <Avatar name={nameOf(b.participantId)} size="sm" />
                        <span className="truncate text-body-md">
                          {nameOf(b.participantId)}
                        </span>
                      </div>
                      <span
                        className={cn(
                          "flex shrink-0 items-center gap-2 text-label-md",
                          owed ? "text-tertiary" : "text-error",
                        )}
                      >
                        {owed ? "is owed" : "owes"}
                        <MoneyAmount
                          minor={Math.abs(b.net)}
                          currency={data.currency}
                          className="text-body-md font-semibold"
                        />
                      </span>
                    </li>
                  );
                })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Section>
  );
}
